import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { PageHeader } from '../components/PageHeader';
import {
  useMe,
  useMemberPermissions,
  useTeam,
  useUpdateMemberPermissions,
} from '../hooks/useOps';
import { PERMISSION_GROUPS, type CapabilityKey } from '../lib/permissions';

/**
 * Per-user capability overrides (MES-style permissions editor). Each checkbox
 * shows the EFFECTIVE value; ticking away from the role default records an
 * override, ticking back removes it. Save writes the sparse override map.
 */
export function TeamPermissionsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: me } = useMe();
  const { data: members = [] } = useTeam();
  const { data: perms, isLoading, error } = useMemberPermissions(id);
  const save = useUpdateMemberPermissions();

  const member = members.find((m) => m.id === id);
  const isSelf = me?.user?.id === id;

  // Draft = effective flags currently shown; overrides derived against defaults.
  const [draft, setDraft] = useState<Record<string, boolean> | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!perms) return;
    const { role: _role, ...flags } = perms.effective;
    setDraft(flags as Record<string, boolean>);
  }, [perms]);

  // Role defaults reconstructed from the server payload: where a key is
  // overridden, the default is the opposite boolean; otherwise it equals the
  // effective value.
  const defaults = useMemo(() => {
    if (!perms || !draft) return null;
    const { role: _role, ...effective } = perms.effective;
    const d: Record<string, boolean> = {};
    for (const [k, v] of Object.entries(effective as Record<string, boolean>)) {
      const o = perms.overrides?.[k];
      d[k] = typeof o === 'boolean' ? !o : v;
    }
    return d;
  }, [perms, draft]);

  const overridesFromDraft = useMemo(() => {
    if (!draft || !defaults) return null;
    const out: Record<string, boolean> = {};
    for (const [k, v] of Object.entries(draft)) {
      if (defaults[k] !== v) out[k] = v;
    }
    return out;
  }, [draft, defaults]);

  const dirty = useMemo(() => {
    const current = perms?.overrides ?? {};
    const next = overridesFromDraft ?? {};
    const keys = new Set([...Object.keys(current), ...Object.keys(next)]);
    return [...keys].some((k) => current[k] !== next[k]);
  }, [perms, overridesFromDraft]);

  const onToggle = (key: CapabilityKey, value: boolean) => {
    setSaved(false);
    setDraft((d) => (d ? { ...d, [key]: value } : d));
  };

  const onSave = async () => {
    if (!id || !overridesFromDraft) return;
    setSaveError(null);
    try {
      await save.mutateAsync({
        id,
        permissions: Object.keys(overridesFromDraft).length ? overridesFromDraft : null,
      });
      setSaved(true);
    } catch (e) {
      setSaveError((e as Error).message);
    }
  };

  const onResetToDefaults = async () => {
    if (!id) return;
    setSaveError(null);
    try {
      await save.mutateAsync({ id, permissions: null });
      setSaved(true);
    } catch (e) {
      setSaveError((e as Error).message);
    }
  };

  if (isLoading || !draft || !perms) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        {error ? <Alert severity="error">{(error as Error).message}</Alert> : <CircularProgress />}
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={`Permissions — ${member?.name ?? perms.role}`}
        action={
          <Stack direction="row" gap={1}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/team')}>
              Back to team
            </Button>
            <Button
              startIcon={<RestartAltIcon />}
              disabled={save.isPending || !perms.overrides || isSelf}
              onClick={onResetToDefaults}
            >
              Reset to role defaults
            </Button>
            <Button
              variant="contained"
              disabled={save.isPending || !dirty || isSelf}
              onClick={onSave}
            >
              Save
            </Button>
          </Stack>
        }
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        Role: <Chip size="small" label={perms.role} sx={{ mx: 0.5 }} /> — checkboxes show this
        member's <strong>effective</strong> capabilities. Changing one away from the role default
        records a per-user override (marked <Chip size="small" color="warning" variant="outlined" label="override" sx={{ mx: 0.5 }} />).
      </Alert>

      {isSelf && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You cannot edit your own permissions.
        </Alert>
      )}
      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {saveError}
        </Alert>
      )}
      {saved && !dirty && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Permissions saved.
        </Alert>
      )}

      <Stack gap={2}>
        {PERMISSION_GROUPS.map((group) => (
          <Paper key={group.label} variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              {group.label}
            </Typography>
            <Stack>
              {group.keys.map(({ key, label, hint }) => {
                const overridden = defaults != null && defaults[key] !== draft[key];
                return (
                  <Stack key={key} direction="row" alignItems="center" gap={1}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={draft[key] ?? false}
                          disabled={isSelf || save.isPending}
                          onChange={(e) => onToggle(key, e.target.checked)}
                        />
                      }
                      label={
                        <Stack direction="row" alignItems="baseline" gap={1}>
                          <Typography variant="body2">{label}</Typography>
                          {hint && (
                            <Typography variant="caption" color="text.secondary">
                              {hint}
                            </Typography>
                          )}
                        </Stack>
                      }
                    />
                    {overridden && (
                      <Chip size="small" color="warning" variant="outlined" label="override" />
                    )}
                  </Stack>
                );
              })}
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
}
